import { getAxiosConfig, axios } from '@config/axios';
import Environment from '@config/environment';
import { ApiResponse } from '@interfaces/api';
import { OrderData, OrderResponse } from '@interfaces/api/responses/my-order';
import {
  GenerateShippingLabel,
  RegenerateShippingLabel,
  SaleAwaitingShipProduct,
  SaleData,
  SaleResponse,
} from '@interfaces/api/responses/my-sale';
import { TimelineResponse } from '@interfaces/api/responses/timeline';
import { EmptyResponse, RelistData, RelistResponse } from '@interfaces/api/responses/timeline-action';
import { ShippingAddressResponse, ShippingAddressRow } from '@interfaces/api/responses/user-shipping-address';
import { ReturnFormParam } from '@interfaces/api/payloads/timeline-action';
import {
  OrderPickupPoint,
  ShippingLabelCarrier,
  SuggestionLocation,
  Timeline,
  TimelineKind,
} from '@interfaces/models/timeline';
import { buildQueryString } from '@helpers/utils/general';

// ResponseResult should be a Result<ApiError, A>
// But we will temporarily use ResponseResult will change when Fenx is type driven development
export type ResponseResult<D> =
  | {
      kind: 'Success';
      data: D;
    }
  | {
      kind: 'NetworkingError';
    }
  | {
      kind: 'InvalidData';
    }
  | {
      kind: 'ServerError';
      message: string | null;
      objects?: unknown[];
    };

export function requestHeaders() {
  return { ...getAxiosConfig(), validateData: () => true }; // validateData to prevent Axios throw when not 200 response
}

function validateData<D>(data: ApiResponse<D>): ResponseResult<D> {
  if (data.data != null) {
    return { kind: 'Success', data: data.data };
  } else if (data.errors != null) {
    return { kind: 'ServerError', message: (data.errors.detail as string) || '', objects: data.errors.objects };
  } else {
    return { kind: 'InvalidData' };
  }
}

// Special case for timeline because timeline require timelineKind
function validateDataTimeline(data: ApiResponse<Timeline>, kind: TimelineKind): ResponseResult<Timeline> {
  if (data.data != null) {
    return { kind: 'Success', data: { ...data.data, kind } };
  } else if (data.errors != null) {
    return { kind: 'ServerError', message: (data.errors.detail as string) || '' };
  } else {
    return { kind: 'InvalidData' };
  }
}

export async function getOrders(
  page: number,
  includeProductId?: string,
  includeOrderId?: string,
): Promise<ResponseResult<OrderData>> {
  try {
    const params = {
      includeProductId,
      includeOrderId,
      page: page.toString(),
    };

    const query = buildQueryString(params);

    const response = await axios.get<OrderResponse>(
      `${Environment.apiBaseUrl}/users/me/orders?${query}`,
      requestHeaders(),
    );

    return validateData(response.data);
  } catch {
    return { kind: 'NetworkingError' };
  }
}

export async function getSales(
  page: number,
  productIds?: number[],
  fromDate?: string,
  toDate?: string,
  includeProductId?: string,
): Promise<SaleData> {
  const productQuery = productIds?.length
    ? `&${productIds.map((id, idx) => `productIds[${idx}]=${id}`).join('&')}`
    : '';

  const params = {
    fromDate,
    toDate,
    includeProductId,
    page: page.toString(),
  };

  const query = buildQueryString(params);

  const response = await axios.get<SaleResponse>(
    `${Environment.apiBaseUrl}/users/me/soldItems?${query}${productQuery}`,
    requestHeaders(),
  );

  return response.data.data;
}

export const getSaleAwaitingShipItems = async (): Promise<SaleAwaitingShipProduct> => {
  const response = await axios.get<SaleAwaitingShipProduct>(
    `${Environment.apiBaseUrl}/users/me/awaiting-shipping-items`,
    requestHeaders(),
  );
  return response.data;
};

export async function getTimeline(productId: string, orderId: string | null): Promise<ResponseResult<Timeline>> {
  try {
    const kind = orderId ? 'Buyer' : 'Seller';
    const type = kind === 'Buyer' ? 'buyer' : 'seller';
    const order = orderId ? `&order=${orderId}` : '';

    const axiosConfig = requestHeaders();

    axiosConfig.headers = {
      ...axiosConfig.headers,
      'x-vc-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    const response = await axios.get<TimelineResponse>(
      `${Environment.apiBaseUrl}/contents/timeline/${type}?product=${productId}${order}&version=3&dataVersion=1`,
      axiosConfig,
    );

    return validateDataTimeline(response.data, kind);
  } catch {
    return { kind: 'NetworkingError' };
  }
}

export async function submitReturnForm(productId: string, param: ReturnFormParam): Promise<ResponseResult<unknown>> {
  try {
    const response = await axios.post<EmptyResponse | ''>(
      `${Environment.apiBaseUrl}/products/${productId}/return-request`,
      param,
      requestHeaders(),
    );

    // This is funny response
    if (response.data === '') {
      return { kind: 'Success', data: null };
    } else {
      return validateData(response.data);
    }
  } catch {
    return { kind: 'NetworkingError' };
  }
}

export async function submitRelist(productId: string): Promise<ResponseResult<RelistData>> {
  try {
    const response = await axios.post<RelistResponse>(
      `${Environment.apiBaseUrl}/products/${productId}/relist`,
      {},
      requestHeaders(),
    );

    return validateData(response.data);
  } catch {
    return { kind: 'NetworkingError' };
  }
}

export async function submitConfirmDelivery(
  tmsShipmentId: string,
  deliveryType: string,
): Promise<ResponseResult<unknown>> {
  try {
    const response = await axios.get<EmptyResponse>(
      `${Environment.apiBaseUrl}/package/status/client-confirmation/shipment/${tmsShipmentId}?deliveryType=${deliveryType}`,
      requestHeaders(),
    );

    return validateData(response.data);
  } catch {
    return { kind: 'NetworkingError' };
  }
}

export const getShippingAddresses = async (): Promise<ShippingAddressRow[]> => {
  const response = await axios.get<ShippingAddressResponse>(
    `${Environment.apiBaseUrl}/users/addressV2?context=account`,
    requestHeaders(),
  );

  return response.data.data;
};

export const getCarriers = async (productId: string): Promise<ShippingLabelCarrier[]> => {
  const response = await axios.get<ApiResponse<ShippingLabelCarrier[]>>(
    `${Environment.apiBaseUrl}/shipments/carriers?productId=${productId}`,
    requestHeaders(),
  );
  return response.data.data;
};

export type SubmitShippingLabelParam = {
  productId: string;
  carrierName: string;
  dropOffType: string;
  shipmentLabelType: string;
};
export const submitShippingLabel = async ({
  productId,
  carrierName,
  dropOffType,
  shipmentLabelType,
}: SubmitShippingLabelParam): Promise<GenerateShippingLabel> => {
  const response = await axios.post<ApiResponse<GenerateShippingLabel>>(
    `${Environment.apiBaseUrl}/products/${productId}/shipments`,
    {
      carrierName,
      dropOffType,
      shipmentLabelType,
    },
    requestHeaders(),
  );

  return response.data.data;
};

export type RegenerateShippingLabelType = {
  productId: string;
};

export const regenerateShippingLabel = async ({
  productId,
}: RegenerateShippingLabelType): Promise<RegenerateShippingLabel> => {
  const response = await axios.post<ApiResponse<RegenerateShippingLabel>>(
    `${Environment.apiBaseUrl}/shipments/address-changes`,
    {
      productId,
    },
    { ...requestHeaders(), timeout: 20000 }, // set timeout: 20000 because this process take extremely long
  );

  return response.data.data;
};

// TODO: The below data type and request are getting from vc-checkout - api-specifications
export type RequestDraftListParams = Partial<{
  suggestionId: string;
  productId: string;
  carrierName: string;
  countryCode: string;
}>;

export const getShippingAddressDropOffPoints = async (
  params: RequestDraftListParams,
): Promise<ResponseResult<OrderPickupPoint[]>> => {
  try {
    const config = requestHeaders();
    const response = await axios.get<ApiResponse<OrderPickupPoint[]>>(
      `${Environment.apiBaseUrl}/shipments/drop-off-points`,
      { ...config, params: { ...config.params, ...params } },
    );

    return validateData(response.data);
  } catch {
    return { kind: 'NetworkingError' };
  }
};

export const getAddressSuggestions = async (
  text: string,
  countryIso2: string,
): Promise<ResponseResult<SuggestionLocation[]>> => {
  try {
    const config = requestHeaders();
    const response = await axios.get<ApiResponse<SuggestionLocation[]>>(
      `${Environment.apiBaseUrl}/address/suggestion?text=${text}&countryIso2=${countryIso2}`,
      { ...config },
    );

    return validateData(response.data);
  } catch {
    return { kind: 'NetworkingError' };
  }
};

export const createZendeskTicket = async (productId: string): Promise<unknown> => {
  const response = await axios.post<EmptyResponse>(
    `${Environment.apiBaseUrl}/shipments/customer-tickets`,
    {
      productId,
    },
    requestHeaders(),
  );

  return response.data;
};
